// Navigation du site : sections des pages et libellés du menu.
//
// LOT 16 du plan SEO. Le pied de page a une colonne qui suit le sujet de la
// page — son « silo ». La section d'une page n'est jamais devinée : c'est une
// valeur DÉCLARÉE, soit par la colonne `pages.section` quand la page a une
// ligne dans le CMS, soit par le gabarit qui la rend (une page commune est
// « demenagement », une fiche opérateur est « adresse »). Aucune regex sur
// l'URL, aucun mot-clé cherché dans le contenu : une détection se trompe en
// silence, une déclaration se relit.
//
// Les valeurs possibles sont fermées, et la même liste est la contrainte CHECK
// de la base (migration 20260906150000). Une page dont la section serait
// absente ou inconnue tombe sur « generique » : jamais de colonne vide.
import donnees from './navigation.json';

export const SECTIONS = ['demenagement', 'garde-meubles', 'international', 'adresse', 'generique'] as const;
export type Section = (typeof SECTIONS)[number];

/** Repli sûr : la section d'une page qui n'en déclare aucune. */
export const SECTION_PAR_DEFAUT: Section = 'generique';

export function estSection(valeur: unknown): valeur is Section {
  return typeof valeur === 'string' && (SECTIONS as readonly string[]).includes(valeur);
}

interface Donnees {
  libelles: { lift: string; internationalAnnonce?: string };
  sections: Record<string, string>;
}

const D = donnees as Donnees;

/**
 * Libellé de l'entrée de menu vers /monte-meubles.
 *
 * « Location de lift » est conditionné à l'arbitrage du lot 5 ; d'ici là,
 * « Monte-meubles ». Le libellé est une donnée (table `navigation`) : le jour
 * où il change, quatre-vingt-dix-neuf ancres identiques changent avec lui
 * sans toucher au code.
 */
export const LIBELLE_LIFT: string = D.libelles.lift?.trim() || 'Monte-meubles';

/**
 * Paragraphe d'annonce du pilier international — lot 17.
 *
 * « La France ouvre la série ; le Luxembourg, les Pays-Bas… suivront » est
 * une phrase qui vieillit à chaque vague de publication. Elle est donc une
 * donnée (table `navigation`, clé `international_annonce`), mise à jour dans
 * Paramètres du site à chaque page pays publiée, sans déploiement de code.
 * Vide en base : le pilier n'annonce rien, plutôt qu'une promesse périmée.
 */
export const ANNONCE_INTERNATIONAL: string = D.libelles.internationalAnnonce?.trim() ?? '';

/**
 * Section déclarée en base pour une page du CMS, par son slug.
 *
 * `undefined` si la page n'a pas de ligne publiée ou si la valeur n'est pas
 * dans la liste fermée — le gabarit décide alors. Une valeur inconnue venue de
 * la base n'est pas une erreur de build : la contrainte CHECK l'empêche
 * d'exister, et si elle existait quand même, « generique » est la bonne
 * réponse.
 */
export function sectionDeclaree(slug: string | undefined): Section | undefined {
  if (!slug) return undefined;
  const v = D.sections[slug];
  return estSection(v) ? v : undefined;
}

/**
 * Section effective d'une page : la colonne si elle existe, sinon le gabarit,
 * sinon le repli. C'est LA règle, écrite une fois.
 */
export function sectionDePage(slug: string | undefined, gabarit: Section | undefined): Section {
  return sectionDeclaree(slug) ?? gabarit ?? SECTION_PAR_DEFAUT;
}

/** Les slugs CMS dont la section est déclarée en base, pour les contrôles. */
export function sectionsDeclarees(): ReadonlyArray<{ slug: string; section: Section }> {
  return Object.entries(D.sections)
    .filter((e): e is [string, Section] => estSection(e[1]))
    .map(([slug, section]) => ({ slug, section }));
}
